'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { persistor } from '../_store'
import { IPoint } from '../_store/common/types'
import { sessionActions } from '../_store/session/session.slice'
import { IRedux } from '../_store/types'
import {
  createPointFromCoordinates,
  findActivePoint,
  getUserLocation,
  shouldCreateNewPoint,
} from '../_utils/location'

export default function useAutoGeolocation() {
  const [rehydrated, setRehydrated] = useState(
    () => persistor.getState().bootstrapped
  )
  const [geoloc, setGeoloc] = useState<[number, number]>([0, 0])
  const [detectedPoint, setDetectedPoint] = useState<IPoint | null>(null)
  const [open, setOpen] = useState(false)
  const hasRequestedGeolocation = useRef(false)

  const dispatch = useDispatch()
  const { points } = useSelector((state: IRedux) => state.common)
  const { is_hall, permissions, order_type } = useSelector(
    (state: IRedux) => state.session
  )

  const savedActivePoint = useMemo(
    () => (is_hall ? null : findActivePoint(points)),
    [is_hall, points]
  )

  const activePoint = useMemo(() => {
    if (is_hall) return null
    return detectedPoint || savedActivePoint
  }, [is_hall, detectedPoint, savedActivePoint])

  useEffect(() => {
    if (rehydrated) return

    const unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) {
        setRehydrated(true)
      }
    })

    if (persistor.getState().bootstrapped) {
      setRehydrated(true)
    }

    return unsubscribe
  }, [rehydrated])

  useEffect(() => {
    if (!shouldCreateNewPoint(geoloc, points, activePoint)) return

    createPointFromCoordinates(geoloc).then((newPoint) => {
      if (newPoint) {
        setDetectedPoint(newPoint)
      }
    })
  }, [geoloc, points, activePoint])

  useEffect(() => {
    if (!rehydrated) return
    if (is_hall === 'true') return
    if (order_type?.type === 'self-pickup') return
    if (permissions.geolocation === 'rejected') return

    if (permissions.geolocation === 'accepted' && activePoint?.address) {
      setOpen(false)
      return
    }

    if (savedActivePoint?.address) {
      if (permissions.geolocation !== 'accepted') {
        setOpen(true)
      }
      return
    }

    if (hasRequestedGeolocation.current) return

    hasRequestedGeolocation.current = true

    getUserLocation()
      .then((location) => {
        if (location?.latitude) {
          setGeoloc([location.latitude, location.longitude])
        } else {
          dispatch(sessionActions.rejectPGL())
          setOpen(true)
          console.warn('Geolocation success but no data')
        }
      })
      .catch((err) => {
        console.log('Failed to get location:', err)
        dispatch(sessionActions.rejectPGL())
        setOpen(true)
      })
  }, [
    rehydrated,
    is_hall,
    order_type?.type,
    permissions.geolocation,
    activePoint?.address,
    savedActivePoint?.address,
    dispatch,
  ])

  useEffect(() => {
    if (
      activePoint?.address &&
      permissions.geolocation !== 'accepted' &&
      permissions.geolocation !== 'rejected'
    ) {
      setOpen(true)
    }
  }, [activePoint?.address, permissions.geolocation])

  const dismissModal = useCallback(() => setOpen(false), [])

  return {
    activePoint,
    open,
    setOpen,
    dismissModal,
    isDisabled: is_hall === 'true' || order_type?.type === 'self-pickup',
  }
}
